import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Alert, Dropdown } from 'react-bootstrap';
import { signInWithEmailAndPassword, sendPasswordResetEmail, sendEmailVerification } from 'firebase/auth';
import { auth } from '../../services/firebase';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import './Auth.css';
import logoIcon from '../../assets/images/logo-icon.png';
import webappImg from '../../assets/images/webapp_img.svg';
import gextLogo from '../../assets/images/gext_logo.svg';
import { useTranslation } from 'react-i18next';

function Login() {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [verificationMessage, setVerificationMessage] = useState('');

  useEffect(() => {
    // Carica la lingua preferita dall'utente se disponibile
    const preferredLanguage = localStorage.getItem('preferredLanguage');
    if (preferredLanguage) {
      i18n.changeLanguage(preferredLanguage);
    }
  }, [i18n]);

  useEffect(() => {
    if (searchParams.get('verification') === 'pending') {
      setVerificationMessage(t('auth.login.verification.pending'));
    }
  }, [searchParams, t]);

  const handleLanguageChange = (language) => {
    i18n.changeLanguage(language);
    localStorage.setItem('preferredLanguage', language);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setLoading(true);
      
      // Effettua il login
      const userCredential = await signInWithEmailAndPassword(
        auth, 
        formData.email, 
        formData.password
      );
      
      // Verifica se l'email è verificata
      if (!userCredential.user.emailVerified) {
        // Non effettuare il logout immediatamente
        setVerificationMessage(t('auth.login.verification.pending'));
        
        try {
          // Invia una nuova email di verifica
          await sendEmailVerification(userCredential.user);
          setVerificationMessage(t('auth.login.verification.resent'));
        } catch (verificationError) {
          if (verificationError.code === 'auth/too-many-requests') {
            setError(t('auth.login.verification.tooManyRequests'));
          } else {
            setError(t('auth.login.verification.error'));
          }
        }
        
        // Solo ora effettua il logout
        await auth.signOut();
        return;
      }
      
      // Se l'email è verificata, aspetta un momento prima di reindirizzare
      // per permettere all'abbonamento di essere caricato
      setTimeout(() => {
        setLoading(false);
        navigate('/', { replace: true });
      }, 1500);
      
    } catch (error) {
      console.error('Login error:', error);
      switch (error.code) {
        case 'auth/invalid-credential':
        case 'auth/user-not-found':
        case 'auth/wrong-password':
          setError(t('auth.login.errors.invalidCredentials'));
          break;
        case 'auth/too-many-requests':
          setError(t('auth.login.errors.tooManyAttempts'));
          break;
        default:
          setError(t('auth.login.errors.generic'));
      }
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!formData.email) {
      return setError(t('auth.login.errors.emailRequired'));
    }

    try {
      setError('');
      setLoading(true);
      await sendPasswordResetEmail(auth, formData.email);
      setResetSent(true);
    } catch (error) {
      setError(t('auth.login.errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="auth-container">
      <div className="auth-left-panel">
        <div className="gext-logo">
          <img src={gextLogo} alt="GEXT Logo" />
        </div>
        <div className="auth-illustration">
          <img src={webappImg} alt="Gestione riparazioni" />
        </div>
        <div className="auth-left-content">
          <h1>{t('auth.login.title').split(' ')[0]}<br />{t('auth.login.title').split(' ').slice(1).join(' ')}</h1>
          <p>{t('auth.login.subtitle')}</p>
        </div>
      </div>

      <div className="auth-right-panel">
        <div className="brand-header-top">
          <div className="brand-logo">
            <img src={logoIcon} alt="PhoneDesk Logo" className="logo-icon" />
            <span>PhoneDesk</span>
          </div>
          <div className="language-selector">
            <Dropdown align="end">
              <Dropdown.Toggle variant="link" id="language-dropdown" className="language-toggle">
                <i className="bi bi-globe"></i>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => handleLanguageChange('it')}>Italiano</Dropdown.Item>
                <Dropdown.Item onClick={() => handleLanguageChange('en')}>English</Dropdown.Item>
                <Dropdown.Item onClick={() => handleLanguageChange('zh')}>中文</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
        <div className="auth-form-container">
          <div className="auth-logo">
            <h2>{t('auth.login.title')}</h2>
          </div>

          {verificationMessage && (
            <Alert variant="info">
              {verificationMessage}
            </Alert>
          )}
          
          {error && <Alert variant="danger">{error}</Alert>}
          {resetSent && (
            <Alert variant="success">
              {t('auth.login.resetPassword.success')}
            </Alert>
          )}
          
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>{t('auth.login.form.email')}</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t('auth.login.form.emailPlaceholder')}
                required
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>{t('auth.login.form.password')}</Form.Label>
              <div className="password-input-group">
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={t('auth.login.form.passwordPlaceholder')}
                  required
                />
                <Button 
                  variant="link" 
                  className="password-toggle"
                  onClick={togglePasswordVisibility}
                >
                  <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                </Button>
              </div>
            </Form.Group>

            <Button
              className="w-100 auth-submit-btn"
              type="submit"
              disabled={loading}
            >
              {t('auth.login.form.submit')}
            </Button>

            <div className="text-center mt-3">
              <Button
                variant="link"
                onClick={handlePasswordReset}
                disabled={loading}
                className="forgot-password-link"
              >
                {t('auth.login.form.forgotPassword')}
              </Button>
            </div>
          </Form>

          <div className="auth-footer">
            {t('auth.login.form.noAccount')} <Link to="/register">{t('auth.login.form.register')}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login; 